import React, { useEffect, useState, useMemo } from "react";
import store from "../../redux/store";
import {
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Spacer,
  Td,
  Tr,
} from "@chakra-ui/react";
import { FiSearch, FiTrash } from "react-icons/fi";
import TableView from "../../components/TableView";
import { useSelector } from "react-redux";
import CustomButton from "../../components/ButtonComp";
import CustomToolTip from "../../components/CustomTooltip";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "react-toastify";
import {
  get_all_inventory_list,
  get_inventory_list,
} from "../../redux/inventorySlice";
import { get_full_product_list } from "../../redux/productSlice";
import { baseUrl } from "../../utils/utils";
import GasCylinderLoader from "../../components/GasCylinderLoader";

const Inventory = () => {
  const location = useLocation();
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [rows, setRows] = useState(10);
  const [page, setpage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { inventory_list, total_inventory_count,loading } = useSelector(
    (state) => state.inventory
  );

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", deleteId);

      const response = await fetch(baseUrl + `inventory.php`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error(`Failed to delete inventory`);

      const result = await response.json();
      if (result?.success) {
        store.dispatch(get_inventory_list({ page, rows }));
        toast.success(result?.message);
        setDeleteDialog(false);
      } else {
        toast.error(result?.message);
      }
    } catch (error) {
      console.error("Error deleting inventory:", error);
      toast.error("Failed to delete inventory");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const restoredPage = location.state?.page || 1;
    const restoredRows = location.state?.rows || 10;
    const restoredInitialValue = location.state?.initialValue || 1;

    setpage(restoredPage);
    setRows(restoredRows);
    setInitialValue(restoredInitialValue);

    store.dispatch(get_inventory_list({ page: restoredPage, rows: restoredRows }));
    store.dispatch(get_full_product_list());
    store.dispatch(get_all_inventory_list());
  }, []);

  // 🔍 Filtered inventory list based on searchTerm 
  const filteredInventory = useMemo(() => {
    if (!searchTerm.trim()) return inventory_list;
    const lowerSearch = searchTerm.toLowerCase();
    return inventory_list.filter(
      (el) =>
        el?.product_name?.toLowerCase().includes(lowerSearch) ||
        el?.category?.toLowerCase().includes(lowerSearch) ||
        el?.type?.toLowerCase().includes(lowerSearch) ||
        el?.opening_date?.toLowerCase().includes(lowerSearch)
    );
  }, [searchTerm, inventory_list]);

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        flexDirection={["column", "row"]}
        alignItems={["stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >

        <Heading fontSize="xl">Inventory</Heading>
        <Flex direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
          flexWrap="wrap">
        <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "sm"]}>
          <Input
            placeholder="Search by Product, Category, Type, or Date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <InputRightElement>
            <FiSearch />
          </InputRightElement>
        </InputGroup>
        
        <CustomButton
          title="Add Inventory"
          onClick={() =>
            navigate("/inventory/add-inventory", {
              state: { inventory_list, page, rows, initialValue },
            })
          }
          w={["100%", "auto"]}
        />
        </Flex>
        
      </Card>

      <div>
        {loading ? (
          <GasCylinderLoader
          size="sm"
          loadingText="Loading Inventories"
          />
        ):(
          <>
          <TableView
          headData={[
            "S.No.",
            "Product Name",
            "Category",
            "Type",
            "OS Fulls",
            "OS Empties",
            "Current Fulls",
            "Current Empties",
            "Defective Return",
            "Security Deposit",
            "Opening Date",
            "Action",
          ]}
          body={filteredInventory.map((el, i) => (
            <Tr key={el.id}>
              <Td textAlign="center">{i + initialValue}</Td>
              <Td textAlign="center">{el.product_name}</Td>
              <Td textAlign="center">{el.category}</Td>
              <Td textAlign="center">{el.type}</Td>
              <Td textAlign="center">{el.os_fulls}</Td>
              <Td textAlign="center">{el.os_emptys}</Td>
              <Td textAlign="center">{el.cs_fulls}</Td>
              <Td textAlign="center">{el.cs_emptys}</Td>
              <Td textAlign="center">{el.defective_return}</Td>
              <Td textAlign="center">{el.securitydeposit}</Td>
              <Td textAlign="center">{el.opening_date}</Td>
              <Td textAlign="center">
                <Flex justify={"center"} gap={4}>
                  <CustomToolTip
                    button={
                      <FiTrash
                        size={20}
                        cursor={"pointer"}
                        color="#F00"
                        onClick={() => {
                          setDeleteId(el.id);
                          setDeleteDialog(true);
                        }}
                      />
                    }
                    mess={"Delete Inventory"}
                  />
                </Flex>
              </Td>
            </Tr>
          ))}
          initialValue={initialValue}
          page={page}
          rows={rows}
          total_value={total_inventory_count}
          pagination
          onChange={(e) => {
            setInitialValue(1);
            setpage(1);
            const newRows = e.target.value;
            setRows(newRows);
            store.dispatch(get_inventory_list({ page: 1, rows: newRows }));
          }}
          onPrevious={() => {
            if (page > 1) {
              setInitialValue(initialValue - rows);
              const newPage = page - 1;
              setpage(newPage);
              store.dispatch(get_inventory_list({ page: newPage, rows }));
            }
          }}
          onNext={() => {
            if (rows * page < total_inventory_count) {
              setInitialValue(initialValue + rows);
              const newPage = page + 1;
              setpage(newPage);
              store.dispatch(get_inventory_list({ page: newPage, rows }));
            }
          }}
        />
        <ConfirmDialog
          isOpen={deleteDialog}
          onClose={() => setDeleteDialog(false)}
          onConfirm={handleDelete}
          type="delete"
          title="Delete Inventory"
          message="Are you sure you want to delete this Inventory? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isLoading}
        />
        </>
        )}
       
      </div>
    </>
  );
};

export default Inventory;
